// Two small window-wide channels between the pages and the shell, so neither
// needs the other's props: "something changed, fetch again" (item writes
// refresh the collections list and its counts, account actions the pending
// badge), and "the shell is about to navigate" (a page with unsaved edits may
// take that over and ask first).
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const CHANGED = 'inventorium:changed';
const NAVIGATE = 'inventorium:navigate';

/** Tells whoever listens that `topic` ('collections', 'accounts', 'invitations') changed. */
export const notifyChanged = (topic) => window.dispatchEvent(new CustomEvent(CHANGED, { detail: { topic } }));

/** Calls `callback` each time `topic` is said to have changed. */
export function useOnChanged(topic, callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    const onChanged = (event) => { if (event.detail && event.detail.topic === topic) callbackRef.current(); };
    window.addEventListener(CHANGED, onChanged);
    return () => window.removeEventListener(CHANGED, onChanged);
  }, [topic]);
}

/**
 * Before a navigation made in code (no link to click, e.g. the search box's
 * Enter): false when a page took it over (it asks, then goes to `to` itself).
 */
export const mayNavigate = (to) => window.dispatchEvent(new CustomEvent(NAVIGATE, { cancelable: true, detail: { to } }));

/** For a page with unsaved edits: `onNavigate(to)` replaces the shell's navigations while `active`. */
export function useShellNavigationGuard(active, onNavigate) {
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  useEffect(() => {
    if (!active) return undefined;
    const onShellNavigate = (event) => {
      event.preventDefault();
      onNavigateRef.current(event.detail.to);
    };
    window.addEventListener(NAVIGATE, onShellNavigate);
    return () => window.removeEventListener(NAVIGATE, onShellNavigate);
  }, [active]);
}

/**
 * Leaving a page with unsaved edits asks first (`ask()` resolves to true to
 * leave): closing or reloading the tab (the browser's own question), following
 * a link of the page or of the shell (held, then followed once confirmed), and
 * the shell's own navigations (the search box). Active while `active`. The
 * browser's Back button is not caught (no data router).
 */
export function useLeaveGuard(active, ask) {
  const navigate = useNavigate();
  const askRef = useRef(ask);
  askRef.current = ask;
  useShellNavigationGuard(active, (to) => {
    askRef.current().then((leave) => { if (leave) navigate(to); });
  });
  useEffect(() => {
    if (!active) return undefined;
    const onUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    // A link let through once confirmed: clicked again, so the router's own
    // handler follows it with everything it carries (its state, replace…).
    let letThrough = null;
    const onClick = (event) => {
      if (letThrough && event.target.closest && event.target.closest('a[href]') === letThrough) {
        letThrough = null;
        return;
      }
      // A new tab or window (middle click, Cmd/Ctrl/Shift/Alt) leaves nothing behind.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.origin !== window.location.origin) return;
      // The link waits for the answer: leave only once confirmed.
      event.preventDefault();
      event.stopPropagation();
      askRef.current().then((leave) => {
        if (!leave) return;
        window.removeEventListener('beforeunload', onUnload);
        if (document.contains(link)) {
          letThrough = link;
          link.click();
        } else {
          document.removeEventListener('click', onClick, true);
          navigate(link.pathname + link.search + link.hash);
        }
      });
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [active, navigate]);
}
